<script setup>
import { computed, inject, onMounted, ref, toValue, watch } from 'vue';
import { useField, FormContextKey } from 'vee-validate';
import { useProjeto } from '@/modules/projeto/composables/useProjeto';
import { camposBuscaConta } from '@/commons/contaBancaria';

const props = defineProps({
    // Vai na div em volta do campo (ex.: alvo de tutorial).
    id: {
        type: String,
        default: undefined
    },
    projetoId: String,
    // Lista pronta de contas (ex.: contas da fundação). Quando informada, substitui
    // as contas carregadas pelo `projetoId`.
    opcoes: {
        type: Array,
        default: null
    },
    carregando: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    // Nome do campo no schema do useForm. Quando informado, o componente grava o id
    // da conta escolhida no formulário e lê o erro sozinho.
    name: {
        type: String,
        default: null
    },
    erro: {
        type: String,
        default: null
    },
    modelValue: {
        type: Number,
        default: null
    },
    titulo: {
        type: String,
        default: 'Conta Bancária'
    },
    placeholder: {
        type: String,
        default: 'Selecione a conta bancária'
    },
    // Texto exibido quando não há contas (ex.: orientar a procurar o setor financeiro).
    mensagemVazia: {
        type: String,
        default: 'Nenhuma conta bancária encontrada'
    },
    // Normalmente dispensável: a obrigatoriedade vem do schema. Serve para forçar o
    // asterisco em regra condicional (`when`), que o schema não expõe estaticamente.
    obrigatorio: {
        type: Boolean,
        default: false
    },
    // Ids de contas que não devem aparecer na lista (ex.: conta já escolhida como origem).
    idsIgnorados: {
        type: Array,
        default: () => []
    },
    classeColuna: {
        type: String,
        default: 'col-12 md:col-6'
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

const campoForm = props.name ? useField(props.name) : null;

const { contasBancarias, carregarContasBancarias } = useProjeto();

const contaSelecionada = ref(null);

// Com `name`, o valor vem do formulário; sem ele, do v-model.
const valorAtual = computed(() => (campoForm ? campoForm.value.value : props.modelValue));

const contasOrigem = computed(() => props.opcoes ?? contasBancarias.value);

const contasDisponiveis = computed(() =>
    contasOrigem.value.filter((conta) => !props.idsIgnorados.includes(conta.id))
);

const rotuloConta = (conta) => {
    if (!conta) return '';
    const agencia = conta.agencia_dv ? `${conta.agencia}-${conta.agencia_dv}` : conta.agencia;
    const operacao = conta.operacao ? ` • Operação ${conta.operacao}` : '';
    return `Agência ${agencia}, Conta ${conta.conta}-${conta.conta_dv}${operacao}`;
};

const sincronizaSelecao = () => {
    if (!valorAtual.value) {
        contaSelecionada.value = null;
        return;
    }

    // Comparação frouxa: o valor pode voltar da API como string e a conta ter id numérico.
    const conta = contasOrigem.value.find((item) => String(item.id) === String(valorAtual.value)) || null;
    if (contaSelecionada.value?.id !== conta?.id) {
        contaSelecionada.value = conta;
    }
};

const selecionaConta = () => {
    const id = contaSelecionada.value ? contaSelecionada.value.id : null;

    if (campoForm) campoForm.handleChange(id);
    emit('update:modelValue', id);
    emit('change', contaSelecionada.value);
};

watch(valorAtual, sincronizaSelecao);

// A lista pronta pode chegar depois do valor (ex.: carregada no onMounted da view).
watch(() => props.opcoes, sincronizaSelecao);

watch(() => props.projetoId, async (novoProjetoId) => {
    if (!novoProjetoId) {
        contaSelecionada.value = null;
        return;
    }

    await carregarContasBancarias(novoProjetoId);
    sincronizaSelecao();
});

// Limpa a seleção caso a conta escolhida passe a ser ignorada.
watch(contasDisponiveis, (contas) => {
    if (contaSelecionada.value && !contas.some((conta) => conta.id === contaSelecionada.value.id)) {
        contaSelecionada.value = null;
        selecionaConta();
    }
});

const mensagemErro = computed(() => props.erro ?? campoForm?.errorMessage.value ?? null);

const formulario = inject(FormContextKey, null);

// O asterisco de obrigatório sai da própria regra do schema (ex.: regraContaBancaria()
// vs regraContaBancaria({ obrigatorio: false })), para não ser declarado duas vezes.
const obrigatorioDoSchema = computed(() => {
    if (!props.name) return null;

    try {
        const campo = toValue(formulario?.schema)?.describe?.()?.fields?.[props.name];
        return campo ? campo.optional === false : null;
    } catch {
        return null;
    }
});

// O schema só acrescenta o asterisco: a prop continua vencendo, porque um `when`
// aparece como opcional no describe() e ainda assim é obrigatório em tempo de uso.
const obrigatorio = computed(() => props.obrigatorio || obrigatorioDoSchema.value === true);

onMounted(async () => {
    if (props.projetoId && !props.opcoes) {
        await carregarContasBancarias(props.projetoId);
    }

    // O valor pode já estar no formulário antes do campo montar (ex.: conta única
    // gravada pela view enquanto o formulário carregava).
    sincronizaSelecao();
});
</script>

<template>
    <div :id="props.id" class="field mb-2" :class="props.classeColuna">
        <SmallTitle :titulo="props.titulo" :obrigatorio="obrigatorio" />
        <Dropdown v-model="contaSelecionada" :options="contasDisponiveis" filter showClear :filterFields="camposBuscaConta()"
            emptyFilterMessage="Nenhum item encontrado" :emptyMessage="props.mensagemVazia"
            :loading="props.carregando" :disabled="props.disabled" :placeholder="props.placeholder" @change="selecionaConta"
            :pt="{ input: { class: 'text-sm' }, item: { class: 'text-sm' } }" :class="{ 'p-invalid': mensagemErro }"
            class="w-full">
            <template #value="slotProps">
                <Transition name="conta-valor" mode="out-in">
                    <div v-if="slotProps.value" :key="slotProps.value.id" class="conta-valor">
                        <div class="flex flex-wrap align-items-center gap-2">
                            <span class="font-semibold text-sm text-800 line-height-2">
                                {{ slotProps.value.banco_nome }}
                            </span>
                            <ApelidoContaTag :conta="slotProps.value" />
                        </div>
                        <span class="text-xs text-500 line-height-2">{{ rotuloConta(slotProps.value) }}</span>
                    </div>
                    <span v-else key="placeholder" class="text-sm line-height-2">
                        {{ slotProps.placeholder }}
                    </span>
                </Transition>
            </template>

            <template #option="slotProps">
                <div class="flex align-items-center gap-2 w-full">
                    <div class="flex flex-column gap-1">
                        <div class="flex flex-wrap align-items-center gap-2">
                            <span class="font-semibold text-sm line-height-2">{{ slotProps.option.banco_nome }}</span>
                            <ApelidoContaTag :conta="slotProps.option" />
                        </div>
                        <span class="text-xs text-500 line-height-2">{{ rotuloConta(slotProps.option) }}</span>
                    </div>
                    <TagBase v-if="!props.opcoes && slotProps.option.contabancaria_id == null" value="Desvinculada" cor="vermelho"
                        negrito classe="ml-auto" />
                </div>
            </template>
        </Dropdown>
        <SmallError v-if="mensagemErro" :error="mensagemErro" />
    </div>
</template>

<style scoped>
/*
 * O campo vazio ocupa uma linha e o preenchido, duas. Em vez de reservar a altura
 * maior o tempo todo, a segunda linha é animada de 0fr para 1fr ao selecionar.
 */
.conta-valor {
    display: grid;
    grid-template-rows: auto 1fr;
}

.conta-valor > * {
    overflow: hidden;
}

.conta-valor-enter-active,
.conta-valor-leave-active {
    transition: grid-template-rows 200ms ease, opacity 150ms ease;
}

.conta-valor-enter-from,
.conta-valor-leave-to {
    grid-template-rows: auto 0fr;
    opacity: 0;
}
</style>
